import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faTrash,
  faSpinner,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import { API_ENDPOINTS } from "../config/constants";
import { formatDistanceToNow } from "date-fns";

const NotificationsPanel = ({ onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchNotifications = async () => {
    try {
      const res = await fetch(
        `${API_ENDPOINTS.NOTIFICATIONS}?page=${page}&limit=10`,
        {
          credentials: "include",
        }
      );
      if (!res.ok) throw new Error("Failed to fetch notifications");

      const data = await res.json();
      setNotifications((prev) =>
        page === 1 ? data.notifications : [...prev, ...data.notifications]
      );
      setHasMore(data.pagination.hasMore);
      setError(null);
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setError("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [page]);

  const markAsRead = async (id) => {
    try {
      const res = await fetch(`${API_ENDPOINTS.NOTIFICATIONS}/${id}/read`, {
        method: "PUT",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to mark notification as read");

      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, read: true } : n))
      );
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const deleteNotification = async (id) => {
    try {
      const res = await fetch(`${API_ENDPOINTS.NOTIFICATIONS}/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete notification");

      setNotifications((prev) => prev.filter((n) => n._id !== id));
    } catch (err) {
      console.error("Error deleting notification:", err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const res = await fetch(`${API_ENDPOINTS.NOTIFICATIONS}/read-all`, {
        method: "PUT",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to mark all notifications as read");

      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-400">
          Notifications
        </h3>
        <button
          onClick={markAllAsRead}
          className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
        >
          Mark all as read
        </button>
      </div>

      <div className="max-h-[400px] overflow-y-auto">
        {loading && page === 1 ? (
          <div className="flex justify-center items-center p-4">
            <FontAwesomeIcon icon={faSpinner} spin className="text-gray-400" />
          </div>
        ) : error ? (
          <div className="flex justify-center items-center p-4 text-red-500">
            <FontAwesomeIcon icon={faExclamationCircle} className="mr-2" />
            {error}
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            No notifications yet
          </div>
        ) : (
          <>
            {notifications.map((notification) => (
              <div
                key={notification._id}
                className={`p-4 border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800 ${
                  !notification.read ? "bg-blue-50 dark:bg-blue-900" : ""
                }`}
              >
                <div className="flex items-start justify-between">
                  <Link
                    to={notification.data.link}
                    onClick={() => {
                      if (!notification.read) markAsRead(notification._id);
                      onClose();
                    }}
                    className="flex-1"
                  >
                    <h4 className="font-medium text-gray-900">
                      {notification.data.title}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {notification.data.body}
                    </p>
                    <span className="text-xs text-gray-400 mt-2 block">
                      {formatDistanceToNow(new Date(notification.createdAt), {
                        addSuffix: true,
                      })}
                    </span>
                  </Link>
                  <div className="flex items-center ml-4">
                    {!notification.read && (
                      <button
                        onClick={() => markAsRead(notification._id)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Mark as read"
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </button>
                    )}
                    <button
                      onClick={() => deleteNotification(notification._id)}
                      className="text-red-600 hover:text-red-800 p-1 ml-1"
                      title="Delete"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {hasMore && (
              <button
                onClick={() => setPage((p) => p + 1)}
                className="w-full p-3 text-blue-600 hover:bg-gray-50 text-sm font-medium"
              >
                {loading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  "Load more"
                )}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationsPanel;
